/**
 * Services Down Page
 *
 * Displayed when required backend services are not running
 */

import { useEffect, useState } from 'react'

import { checkServicesHealth, type ServiceHealth } from '../services/health-check'

interface ServiceStatusProps {
  name: string
  description: string
  isHealthy: boolean
}

function ServiceStatus({ name, description, isHealthy }: ServiceStatusProps): JSX.Element {
  return (
    <div className="flex items-center justify-between p-3 bg-surface rounded-lg border border-border">
      <div className="flex items-center gap-3">
        <div className={`w-3 h-3 rounded-full ${isHealthy ? 'bg-green-500' : 'bg-red-500'}`} />
        <div>
          <p className="font-medium text-foreground text-sm">{name}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <span className={`text-sm font-medium ${isHealthy ? 'text-green-600' : 'text-red-600'}`}>{isHealthy ? 'Running' : 'Down'}</span>
    </div>
  )
}

function TechnicalDetails({ health }: { health: ServiceHealth }): JSX.Element {
  return (
    <div className="space-y-2">
      <h2 className="font-semibold text-foreground mb-3">Service Status:</h2>
      <div className="space-y-2">
        <ServiceStatus description="Core accounting functionality" isHealthy={health.services.accounting} name="Accounting Server" />
        <ServiceStatus description="Company and entity management" isHealthy={health.services.entity} name="Entity Service" />
        <ServiceStatus description="Location and timezone features" isHealthy={health.services.geolocation} name="Geolocation Service" />
        <ServiceStatus description="Currency list and exchange rates" isHealthy={health.services.currencyRates} name="Currency Rates Service" />
      </div>
      <div className="mt-6 bg-surface-secondary border border-border rounded-lg p-4">
        <h3 className="font-semibold text-foreground mb-2 text-sm">For System Administrators</h3>
        <div className="space-y-2 text-xs text-foreground-secondary">
          <div><p className="font-medium mb-1">Start services:</p><code className="block bg-surface-secondary px-2 py-1 rounded font-mono">docker-compose up -d</code></div>
          <div><p className="font-medium mb-1">Check status:</p><code className="block bg-surface-secondary px-2 py-1 rounded font-mono">docker-compose ps</code></div>
          <div><p className="font-medium mb-1">View logs:</p><code className="block bg-surface-secondary px-2 py-1 rounded font-mono">docker-compose logs -f</code></div>
        </div>
      </div>
    </div>
  )
}

function HealthToggle({ health, isVisible, onToggle }: { health: ServiceHealth; isVisible: boolean; onToggle: () => void }): JSX.Element {
  return (
    <div className="mb-6">
      <button className="text-sm text-foreground-secondary hover:text-foreground underline mb-3" onClick={onToggle}>
        {isVisible ? 'Hide technical details' : 'Show technical details'}
      </button>
      {isVisible && <TechnicalDetails health={health} />}
    </div>
  )
}

function isAllHealthy(health: ServiceHealth | null): boolean {
  return Boolean(health?.services.accounting && health?.services.geolocation && health?.services.entity && health?.services.currencyRates)
}

interface HeaderProps {
  isSomeServicesDown: boolean
}

function PageHeader({ isSomeServicesDown }: HeaderProps): JSX.Element {
  return (
    <div className="text-center mb-8">
      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${isSomeServicesDown ? 'bg-yellow-100' : 'bg-red-100'}`}>
        <svg className={`w-8 h-8 ${isSomeServicesDown ? 'text-yellow-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      </div>
      <h1 className="text-3xl font-bold text-foreground mb-2">{isSomeServicesDown ? 'Some Services Unavailable' : 'Service Temporarily Unavailable'}</h1>
      <p className="text-foreground-secondary">
        {isSomeServicesDown ? 'Some features may be limited while we work to restore full functionality.' : "We're experiencing technical difficulties. Please try again in a few moments."}
      </p>
    </div>
  )
}

interface ActionButtonsProps {
  isChecking: boolean
  onCheck: () => void
}

function ActionButtons({ isChecking, onCheck }: ActionButtonsProps): JSX.Element {
  return (
    <div className="flex gap-3">
      <button className="flex-1 bg-primary-solid text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 disabled:bg-muted-foreground disabled:cursor-not-allowed font-medium transition-colors" disabled={isChecking} onClick={onCheck}>
        {isChecking ? 'Checking...' : 'Check Again'}
      </button>
      <button className="flex-1 bg-muted text-foreground px-6 py-3 rounded-lg hover:bg-muted font-medium transition-colors" onClick={() => window.location.reload()}>Reload App</button>
    </div>
  )
}

export function ServicesDownPage(): JSX.Element {
  const [health, setHealth] = useState<ServiceHealth | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const [isTechnicalDetailsVisible, setShowTechnicalDetails] = useState(false)

  const checkHealth = async (): Promise<void> => {
    setIsChecking(true)
    const result = await checkServicesHealth()
    setHealth(result)
    setIsChecking(false)
  }

  useEffect(() => { void checkHealth() }, [])

  const isSomeServicesDown = Boolean(health && !isAllHealthy(health) && health.services.accounting)

  return (
    <div className="h-screen overflow-y-auto bg-surface-secondary flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-surface rounded-lg shadow-lg p-8">
        <PageHeader isSomeServicesDown={isSomeServicesDown} />
        {isSomeServicesDown && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <p className="text-sm text-blue-900"><strong>What you can do:</strong> Core accounting features are available. Some advanced features like company setup and location services may be temporarily limited.</p>
          </div>
        )}
        {health && <HealthToggle health={health} isVisible={isTechnicalDetailsVisible} onToggle={() => setShowTechnicalDetails(!isTechnicalDetailsVisible)} />}
        <ActionButtons isChecking={isChecking} onCheck={() => { void checkHealth() }} />
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>Need help? Contact your system administrator or{' '}
            <a className="text-primary hover:text-primary/90 underline" href="https://github.com/anthropics/claude-code/issues">report an issue</a>.
          </p>
        </div>
      </div>
    </div>
  )
}
